import React, { useEffect, useMemo, useState } from 'react'
import {
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  Button,
  Alert,
  Box,
  LinearProgress,
  Divider,
  CircularProgress,
  Chip,
} from '@mui/material'
import { useAuth } from '../../../contexts/AuthContext'
import PageHeader from '../../../components/compliance/layout/PageHeader'

type Job = {
  id: string
  status: string
  stage: string
  progress: number
  document_id: string
  filename: string
  error_message?: string
}

export default function DocumentsPage() {
  const { token } = useAuth()
  const [file, setFile] = useState<File | null>(null)
  const [meta, setMeta] = useState({
    document_type: 'NCC',
    service_line: 'compliance',
    jurisdiction: 'VIC',
    version_label: 'NCC 2022',
    effective_from: '2022-05-01',
    effective_to: ''
  })
  const [msg, setMsg] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [jobs, setJobs] = useState<Job[]>([])
  const [tick, setTick] = useState(0)
  const [loadingJobs, setLoadingJobs] = useState(false)
  const [uploading, setUploading] = useState(false)

  const headers = useMemo(() => (token ? { Authorization: `Token ${token}` } : {}), [token])

  const fetchJobs = async () => {
    if (!token) return
    setLoadingJobs(true)
    try {
      const res = await fetch(`/api/v1/documents/ingestion-jobs/`, { headers: headers as any })
      const data = await res.json().catch(() => [])
      if (res.ok) setJobs(data)
    } finally {
      setLoadingJobs(false)
    }
  }

  useEffect(() => { fetchJobs() }, [token, tick])

  useEffect(() => {
    const active = jobs.some(j => !['published','failed','cancelled'].includes(j.status))
    if (!active) return
    const t = setInterval(() => setTick(x => x + 1), 2000)
    return () => clearInterval(t)
  }, [jobs])

  const cancelJob = async (jobId: string) => {
    if (!token) return
    try {
      const res = await fetch(`/api/v1/documents/ingestion-jobs/${jobId}/cancel/`, {
        method: 'POST',
        headers: headers as any
      })
      if (res.ok) {
        setTick(x => x + 1)
      } else {
        const data = await res.json().catch(() => ({}))
        setErr(data?.detail || 'Failed to cancel job')
      }
    } catch (e) {
      setErr('Error cancelling job')
    }
  }

  const clearQueue = async () => {
    if (!token) return
    try {
      const res = await fetch(`/api/v1/documents/ingestion-jobs/`, {
        method: 'POST',
        headers: headers as any,
        body: JSON.stringify({ document_ids: jobs.filter(j => ['published', 'failed', 'cancelled'].includes(j.status)).map(j => j.id) })
      })
      if (res.ok) {
        setTick(x => x + 1)
      } else {
        const data = await res.json().catch(() => ({}))
        setErr(data?.detail || 'Failed to clear queue')
      }
    } catch (e) {
      setErr('Error clearing queue')
    }
  }

  const upload = async () => {
    if (!file) return
    setMsg(null); setErr(null)
    setUploading(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      Object.entries(meta).forEach(([k,v]) => fd.append(k, v))
      const res = await fetch(`/api/v1/documents/upload/`, {
        method: 'POST',
        headers: headers as any,
        body: fd
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        setErr(data?.detail || 'Upload failed')
        return
      }
      setMsg(`Uploaded. Ingestion job: ${data.ingestion_job_id}`)
      setFile(null)
      setTick(x => x + 1)
    } finally {
      setUploading(false)
    }
  }

  return (
    <Stack spacing={3}>
      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <PageHeader
            title="Document Upload"
            subtitle="Upload a PDF for ingestion and indexing."
          />
          {msg ? <Alert severity="success" sx={{ mb: 2 }}>{msg}</Alert> : null}
          {err ? <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert> : null}
          <Stack spacing={2}>
            <Button variant="outlined" component="label">
              Select PDF
              <input hidden type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            </Button>
            <TextField label="Document Type" value={meta.document_type} onChange={(e)=>setMeta({...meta, document_type:e.target.value})} />
            <TextField
              label="Service line"
              select
              SelectProps={{ native: true }}
              value={meta.service_line}
              onChange={(e)=>setMeta({...meta, service_line:e.target.value})}
            >
              <option value="compliance">Building compliance</option>
              <option value="selling">Sales</option>
              <option value="letting">Lettings</option>
              <option value="managing">Property management</option>
            </TextField>
            <TextField label="Jurisdiction" value={meta.jurisdiction} onChange={(e)=>setMeta({...meta, jurisdiction:e.target.value})} />
            <TextField label="Version Label" value={meta.version_label} onChange={(e)=>setMeta({...meta, version_label:e.target.value})} />
            <TextField label="Effective From (YYYY-MM-DD)" value={meta.effective_from} onChange={(e)=>setMeta({...meta, effective_from:e.target.value})} />
            <TextField label="Effective To (optional)" value={meta.effective_to} onChange={(e)=>setMeta({...meta, effective_to:e.target.value})} />
            <Button variant="contained" disabled={!file || !token || uploading} onClick={upload}>
              {uploading ? 'Uploading…' : 'Upload & Ingest'}
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined" sx={{ borderRadius: 3 }}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <PageHeader title="Ingestion Jobs" subtitle="Status updates while processing." />
            <Button variant="outlined" color="secondary" size="small" onClick={clearQueue} sx={{ mt: 1 }}>
              Clear Inactive
            </Button>
          </Stack>
          {loadingJobs ? <LinearProgress sx={{ my: 2 }} /> : <Divider sx={{ my: 2 }} />}
          <Stack spacing={2}>
            {loadingJobs ? (
              <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
            ) : jobs.length === 0 ? (
              <Typography>No ingestion jobs yet.</Typography>
            ) : null}
            {jobs.map(j => (
              <Box key={j.id} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 2 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography fontWeight={700}>{j.filename}</Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Chip label={j.status} size="small" variant="outlined" />
                    <Typography variant="body2" color="text.secondary">{j.stage}</Typography>
                    {!['published', 'failed', 'cancelled'].includes(j.status) && (
                      <Button size="small" color="error" variant="outlined" onClick={() => cancelJob(j.id)}>
                        Cancel
                      </Button>
                    )}
                  </Stack>
                </Stack>
                <Box sx={{ mt: 1 }}>
                  <LinearProgress variant="determinate" value={Math.max(0, Math.min(100, j.progress || 0))} />
                  <Typography variant="caption" color="text.secondary">
                    {j.progress}% {j.error_message ? `• ${j.error_message}` : ''}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  )
}
